import React, { type FormEvent, useCallback, useEffect, useRef } from 'react';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlags } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { EditorExperimentsConfig } from '@atlaskit/tmp-editor-statsig/experiments-config';
import { token } from '@atlaskit/tokens';
import { type GenerateImageProps, useAIGeneratedCoverImage } from '@atlassian/ai-image-generation';
import {
	ButtonConfig,
	ButtonStyle,
	CommonLayout,
	type OnSubmitPayload,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import {
	MediaPickerSection,
	type SelectedImage,
	type UploadedImage,
} from '@atlassian/company-hub-common/media-components';

import { type SpotlightActions, type SpotlightParameters } from '../../../../../types';

import { i18n } from './messages';

const CC_PAGE_EXPERIENCES_PREMIUM_MACROS_IMG_GENERATION =
	'cc_page_experiences_premium_macros_img_generation' as keyof EditorExperimentsConfig;

const wrapperStyles = xcss({
	paddingBlock: 'space.100',
});

type TextAndImageSectionProps = {
	actions: SpotlightActions;
	contentId: string;
	parameters: SpotlightParameters;
};

export const TextAndImageSection = ({
	parameters,
	actions,
	contentId,
}: TextAndImageSectionProps): React.JSX.Element => {
	const intl = useIntl();
	const { cloudId, enableLinkInputInMediaPicker } = useProductDependencies();
	// Gate the Premium Extensions image generation experiment behind the
	// `confluence-ai` feature so that AI-related UI is suppressed for IC
	// (Isolated Cloud) tenants where Confluence AI is not available.
	const isAiImgGenEnabled =
		expValEquals(CC_PAGE_EXPERIENCES_PREMIUM_MACROS_IMG_GENERATION, 'isEnabled', true) &&
		isFeatureEnabled('confluence', 'confluence-ai');
	const {
		generateImage,
		aiHeaderImageFileId,
		aiHeaderImageStatus,
		error: aiHeaderImageError,
	} = useAIGeneratedCoverImage({
		contentId,
	});

	const { showFlag } = useFlags();
	const { formatMessage } = intl;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Fire an analytics event when an AI-generated header image has been
	// produced successfully. We fire once per generation by tracking when
	// `aiHeaderImageFileId` transitions from falsy to truthy. Mirrors the
	// `headerImageAdded` analytics fired by Confluence's
	// EditorPageCoverPicture.handleAIGeneratedUpload, but adapted for the
	// spotlight extension config panel.
	const prevAIHeaderImageFileIdRef = useRef<string | undefined | null>(null);
	useEffect(() => {
		if (!isAiImgGenEnabled) {
			prevAIHeaderImageFileIdRef.current = aiHeaderImageFileId;
			return;
		}

		const prevFileId = prevAIHeaderImageFileIdRef.current;
		const isNewFileId = !prevFileId && !!aiHeaderImageFileId;
		prevAIHeaderImageFileIdRef.current = aiHeaderImageFileId;

		if (!isNewFileId) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'added',
				actionSubject: 'image',
				actionSubjectId: 'companyHubImageAdded',
				source: 'spotlightExtension',
				attributes: {
					imageSource: 'aiGenerated',
					imageId: aiHeaderImageFileId,
					inputMethod: 'configPanel',
					aiHeaderImageStatus,
					isAIImgGenEnabled: isAiImgGenEnabled,
				},
			},
		}).fire();
	}, [aiHeaderImageFileId, aiHeaderImageStatus, isAiImgGenEnabled, createAnalyticsEvent]);

	// Surface a flag to the user when AI image generation fails. We only fire
	// when the status transitions INTO `ERROR` (mirroring the Confluence header
	// image behaviour in EditorPageCoverPicture) so re-renders while already in
	// the error state don't produce duplicate flags.
	//
	// Each fire uses a unique flag `id` so that consecutive failures STACK in
	// the flag tray (matching the Confluence header image behaviour) rather
	// than replacing the previous flag — ADS `useFlags` dedupes by `id`.
	const prevAIHeaderImageStatusRef = useRef<typeof aiHeaderImageStatus | null>(null);
	const aiGenerationErrorFlagCounterRef = useRef(0);
	useEffect(() => {
		if (!isAiImgGenEnabled) {
			prevAIHeaderImageStatusRef.current = aiHeaderImageStatus;
			return;
		}

		const prevStatus = prevAIHeaderImageStatusRef.current;
		const isTransitioningToError = prevStatus !== 'ERROR' && aiHeaderImageStatus === 'ERROR';
		prevAIHeaderImageStatusRef.current = aiHeaderImageStatus;

		if (!isTransitioningToError) {
			return;
		}

		const description = aiHeaderImageError
			? formatMessage(i18n.aiGenerationErrorFlagDescriptionWithReason, {
					errorReason: aiHeaderImageError,
				})
			: formatMessage(i18n.aiGenerationErrorFlagDescription);

		aiGenerationErrorFlagCounterRef.current += 1;
		void showFlag({
			id: `spotlight-extension-ai-generation-error-flag-${aiGenerationErrorFlagCounterRef.current}`,
			title: formatMessage(i18n.aiGenerationErrorFlagTitle),
			description,
			icon: <StatusErrorIcon label="" size="medium" color={token('color.icon.danger')} />,
		});
	}, [aiHeaderImageStatus, aiHeaderImageError, isAiImgGenEnabled, showFlag, formatMessage]);

	const {
		content: {
			text: { title, description },
			image,
			button: { text: buttonText, link: buttonLink, style: buttonStyle },
		},
		layout,
	} = parameters;
	const {
		updateContentTextTitle,
		updateContentTextDescription,
		updateContentImage,
		updateContentButtonText,
		updateContentButtonLink,
		updateContentIsButtonShown,
		updateContentButtonStyle,
	} = actions;

	const isAiHeaderImageLoading =
		(aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW') &&
		!aiHeaderImageFileId;

	const generateImageRef = useRef(generateImage);
	generateImageRef.current = generateImage;

	const generateAndApplyImage = useCallback(
		async (props: GenerateImageProps) => {
			const mediaFileId = await generateImageRef.current(props);
			if (mediaFileId) {
				updateContentImage(mediaFileId, '');
			}
			return mediaFileId;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[updateContentImage],
	);

	const handleFieldAnalytics = useCallback(
		(fieldType: string, label: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'focused',
					actionSubject: fieldType,
					actionSubjectId: `configPanel${label}TextField`,
					source: 'spotlightExtensionConfig',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleChangeTitle = useCallback(
		(event: FormEvent<HTMLInputElement>) => {
			updateContentTextTitle(event.currentTarget.value);
		},
		[updateContentTextTitle],
	);

	const handleChangeDescription = useCallback(
		(event: FormEvent<HTMLTextAreaElement>) => {
			updateContentTextDescription(event.currentTarget.value);
		},
		[updateContentTextDescription],
	);

	const handleChangeImage = useCallback(
		(props: SelectedImage | UploadedImage) => {
			const imageSrc = 'fullSizeImageUrl' in props ? props.fullSizeImageUrl : props.uploadedImageId;
			const altText = props.altDescription;

			updateContentImage(imageSrc, altText);
		},
		[updateContentImage],
	);

	const handleChangeLink = useCallback(
		(linkPayload: OnSubmitPayload) => {
			if (!buttonLink) {
				updateContentIsButtonShown(true);
			}
			updateContentButtonLink(linkPayload.url);
		},
		[updateContentButtonLink, updateContentIsButtonShown, buttonLink],
	);

	const handleClearLink = useCallback(() => {
		updateContentButtonLink('');
		updateContentIsButtonShown(false);
	}, [updateContentButtonLink, updateContentIsButtonShown]);

	const handleRemoveButton = useCallback(() => {
		updateContentButtonLink('');
		updateContentButtonStyle(ButtonStyle.DEFAULT);
		updateContentButtonText('');
		updateContentIsButtonShown(false);
	}, [
		updateContentButtonLink,
		updateContentIsButtonShown,
		updateContentButtonText,
		updateContentButtonStyle,
	]);

	return (
		<Stack space="space.200" xcss={wrapperStyles}>
			<Stack space="space.050">
				<Text>
					<FormattedMessage {...i18n.titleLabel} />
				</Text>
				<TextField
					maxLength={100}
					value={title}
					onChange={handleChangeTitle}
					onFocus={() => handleFieldAnalytics('textField', 'Title')}
					placeholder={intl.formatMessage(i18n.titlePlaceholder)}
				/>
			</Stack>
			<Stack space="space.050">
				<Text>
					<FormattedMessage {...i18n.descriptionLabel} />
				</Text>
				<TextArea
					minimumRows={5}
					maxLength={180}
					value={description}
					onChange={handleChangeDescription}
					onFocus={() => handleFieldAnalytics('textArea', 'Description')}
					placeholder={intl.formatMessage(i18n.descriptionPlaceholder)}
				/>
			</Stack>
			{layout !== CommonLayout.NO_IMAGE_BACKGROUND && (
				<Inline spread="space-between" alignBlock="center">
					<Text>
						<FormattedMessage {...i18n.imageLabel} />
					</Text>
					<MediaPickerSection
						shouldAllowLinkFromUrl={enableLinkInputInMediaPicker}
						contentId={contentId}
						image={image}
						onChangeImage={handleChangeImage}
						analyticsSource="spotlightExtension"
						generateAIHeaderImage={isAiImgGenEnabled ? generateAndApplyImage : undefined}
						isAiHeaderImageLoading={isAiImgGenEnabled ? isAiHeaderImageLoading : false}
					/>
				</Inline>
			)}

			<ButtonConfig
				cloudId={cloudId}
				link={buttonLink}
				text={buttonText}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-1876
				style={buttonStyle}
				updateButtonText={updateContentButtonText}
				updateButtonStyle={updateContentButtonStyle}
				handleClearButton={handleRemoveButton}
				handleClearLink={handleClearLink}
				handleSelectLink={handleChangeLink}
				source="spotlightExtension"
			/>
		</Stack>
	);
};
